import { App, Button, Card, Popconfirm, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { McpSessionDto } from "@audit-plan/shared";
import { api, ApiError } from "../api/client";
import { fmtDT } from "./pills";
import { ResponsiveList } from "./ResponsiveList";
import { Sensitive } from "./Sensitive";

/**
 * Accessi MCP: sessioni attive di Claude collegato ad Audit Plan (persona + client), con revoca.
 * scope "all" (Amministrazione, solo admin): tutte; "mine" (Sicurezza): le proprie.
 */
export function McpSessionsCard({ scope }: { scope: "mine" | "all" }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mcp-sessions", scope], queryFn: () => api.get<McpSessionDto[]>("/mcp-sessions", { scope }) });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/mcp-sessions/${id}`),
    onSuccess: () => {
      void message.success(t("mcp_revoked"));
      void qc.invalidateQueries({ queryKey: ["mcp-sessions"] });
    },
    onError: (e) => void message.error(e instanceof ApiError ? e.message : t("a_loadFail")),
  });
  const action = (s: McpSessionDto) => (
    <Popconfirm title={t("mcp_revokeConfirm")} onConfirm={() => revoke.mutate(s.id)}>
      <Button danger size="small">
        {t("api_revoke")}
      </Button>
    </Popconfirm>
  );
  return (
    <Card title={t("mcp_accessTitle")}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {t(scope === "all" ? "mcp_accessHelpAll" : "mcp_accessHelpMine")}
      </Typography.Paragraph>
      <ResponsiveList<McpSessionDto>
        rowKey="id"
        size="small"
        loading={q.isLoading}
        dataSource={q.data ?? []}
        pageSize={false}
        emptyText={t("mcp_accessEmpty")}
        tableProps={{ scroll: { x: true } }}
        card={{
          title: (s) => s.clientName,
          subtitle: (s) => (scope === "all" ? <Sensitive>{`${s.userName} · ${s.userEmail}`}</Sensitive> : undefined),
          fields: (s) => [
            [t("mcp_lastUse"), fmtDT(s.lastUsedAt)],
            [t("api_expires"), fmtDT(s.expiresAt)],
          ],
          footer: action,
        }}
        columns={[
          ...(scope === "all" ? [{ title: t("mcp_person"), render: (_: unknown, s: McpSessionDto) => <Sensitive block>{s.userName}<div style={{ fontSize: 12, opacity: 0.65 }}>{s.userEmail}</div></Sensitive> }] : []),
          { title: t("mcp_client"), dataIndex: "clientName" },
          { title: t("mcp_connectedAt"), dataIndex: "createdAt", render: fmtDT },
          { title: t("mcp_lastUse"), dataIndex: "lastUsedAt", render: fmtDT },
          { title: t("api_expires"), dataIndex: "expiresAt", render: fmtDT },
          { title: "", align: "right" as const, render: (_: unknown, s: McpSessionDto) => action(s) },
        ]}
      />
    </Card>
  );
}
